// src/services/homeData.js
//
// Pure rules behind the personal home page (/home, UI revamp step 2). No
// Firebase here: HomePage.js loads the data with the existing loaders and
// hands it to these functions. Nothing on this page writes anything.
//
// "Me" on the roster: the `coordinators` entry whose email is the login email
// (the weekly report's own match, WeeklySurvey.js). Studies, patient boxes and
// calendar visits refer to coordinators by NAME, so "mine" means the roster
// name or any earlier name in its `nameHistory` (a rename keeps old ones).

import { COORDINATOR_BOX_STAGES } from "../constants/patientBoxes";
import { normalizeBackupCrcs } from "./backupCrc";
import { coordinatorForLead } from "./crcNames";

const norm = (s) => String(s || "").trim().toLowerCase();

/** "Good morning" (5:00–11:59), "Good afternoon" (12:00–16:59), else "Good evening". */
export const greetingFor = (date = new Date()) => {
  const h = date.getHours();
  if (h >= 5 && h < 12) return "Good morning";
  if (h >= 12 && h < 17) return "Good afternoon";
  return "Good evening";
};

/** First name from "Ann Smith" or "Smith, Ann". */
export const firstNameOf = (name) => {
  const raw = String(name || "").trim();
  if (!raw) return "";
  if (raw.includes(",")) return (raw.split(",")[1] || "").trim().split(/\s+/)[0] || raw.split(",")[0].trim();
  return raw.split(/\s+/)[0];
};

/** The roster entry for this login email, or null. */
export const rosterEntryFor = (coordinators, email) => {
  const me = norm(email);
  if (!me) return null;
  return (coordinators || []).find((c) => norm(c && c.email) === me) || null;
};

/** Every name this person has gone by on the roster, lower-cased. */
export const myNames = (entry) => {
  const names = new Set();
  if (!entry) return names;
  [entry.name, ...(Array.isArray(entry.nameHistory) ? entry.nameHistory : [])].forEach((n) => {
    if (norm(n)) names.add(norm(n));
  });
  return names;
};

/**
 * Who to greet: the roster name, else the account's display name, else a name
 * the person typed on this page (kept in their browser), else nobody ("").
 */
export const greetingName = ({ rosterName, authName, typedName }) =>
  firstNameOf(rosterName) || firstNameOf(authName) || String(typedName || "").trim();

/** localStorage key for the typed name: per person + company + site. */
export const homeNameKey = (uid, tenantId, siteId) => `regx:homeName:v1:${uid}:${tenantId}:${siteId}`;

const ACTIVE = new Set(["active", "active/nr"]);
const isActiveStudy = (s) => ACTIVE.has(norm(s && s.STATUS));

/**
 * Studies where this person is Lead CRC or a Backup CRC. Active studies first,
 * then by name. `role` is "Lead" or "Backup" (Lead wins if both).
 * Lead CRC on a study is usually a full name ("JALAK UPADHYAY"): it is this
 * person's when the CRC names list links it to them, or it is exactly one of
 * their names (crcNames.coordinatorForLead). Exact only, nothing is guessed.
 */
export const myStudies = (studies, names, { links = [], roster = [] } = {}) => {
  if (!names || !names.size) return [];
  const out = [];
  (studies || []).forEach((s) => {
    if (!s) return;
    const leadName = coordinatorForLead(s.LEAD_CRC, links, roster);
    const lead = names.has(norm(s.LEAD_CRC)) || (!!leadName && names.has(norm(leadName)));
    const backup = normalizeBackupCrcs(s.BACKUP_CRC).some((n) => names.has(norm(n)));
    if (lead || backup) out.push({ study: s, role: lead ? "Lead" : "Backup" });
  });
  return out.sort(
    (a, b) =>
      Number(isActiveStudy(b.study)) - Number(isActiveStudy(a.study)) ||
      String(a.study.NAME || a.study.STUDY_ID || "").localeCompare(String(b.study.NAME || b.study.STUDY_ID || ""))
  );
};

/**
 * This person's own coordinator boxes, from the patients' stored status
 * `"<Stage> (<Name>)"` (constants/patientBoxes.js). Returns one entry per stage,
 * in the CRC page's order, each with its patients (possibly none).
 */
export const myBoxes = (patients, names) => {
  const byStage = new Map(COORDINATOR_BOX_STAGES.map((stage) => [stage, []]));
  if (names && names.size) {
    (patients || []).forEach((p) => {
      const m = /^(.+?)\s*\(([^)]*)\)\s*$/.exec(String((p && p.status) || "").trim());
      if (!m) return;
      const stage = COORDINATOR_BOX_STAGES.find((s) => norm(s) === norm(m[1]));
      if (stage && names.has(norm(m[2]))) byStage.get(stage).push(p);
    });
  }
  return COORDINATOR_BOX_STAGES.map((stage) => ({ stage, patients: byStage.get(stage) }));
};

/** Sunday 00:00 of the date's week (the Research Calendar's own week). */
export const weekStartOf = (date = new Date()) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - d.getDay());
  return d;
};

/**
 * This person's calendar visits in the week starting `start`: events whose
 * coordinator email is the login email, or whose coordinator name is one of
 * theirs. Events typed straight into Google Calendar with no coordinator can't
 * be matched. Sorted by start time.
 */
export const myWeekVisits = (events, { email, names }, start) => {
  const me = norm(email);
  const from = start.getTime();
  const to = from + 7 * 24 * 60 * 60 * 1000;
  return (events || [])
    .map((e) => ({ ...e, startDate: new Date(e && e.start), endDate: new Date(e && e.end) }))
    .filter((e) => !Number.isNaN(e.startDate.getTime()))
    .filter((e) => e.startDate.getTime() >= from && e.startDate.getTime() < to)
    .filter((e) => (me && norm(e.coordinatorEmail) === me) || (names && names.has(norm(e.coordinatorName))))
    .sort((a, b) => a.startDate - b.startDate);
};
